import { Component, ViewEncapsulation, OnInit } from '@angular/core';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Schedule } from 'src/providers/schedule/schedule';

import { Loading } from 'src/shared/components/loading/loading';

import { Util } from 'src/shared/util/util';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

export interface ShareJobDetails {
  bookingId: number;
  serviceName: string;
  date: string;
  startTime: string;
  proFirstName: string;
  proPhone: string;
}

@Component({
    templateUrl: 'share-job.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class ShareJobPage implements OnInit {

  addressName: string;
  copied: boolean;
  errorMessage: string;
  job: ShareJobDetails;
  jobLink: string;
  pageTitle: string;
  dialogParams: any;
  isRightSideContent = true;
  isPastJobLink: boolean;
  loaded: boolean;

  boundCopyLink = this.copyLink.bind(this);

  constructor(
    private navCtrl: CustomNavController,
    private schedule: Schedule,
    private util: Util,
    private storage: TidyStorage,
    private rightSidePanelService: RightSidePanelService,
  ) {}

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    this.dialogParams = await this.storage.retrieve('dialog-params');
    this.rightSidePanelService.setDialogPageTitle('Send Link');
    this.loadData();
  }

  @Loading('', true)
  async loadData() {
    try {
      this.rightSidePanelService.setDialogLoading(true);
      this.isPastJobLink = this.getParam('isPastJobLink') === true;
      this.addressName = this.getParam('addressName');
      this.job = this.isPastJobLink
        ? this.parsePastJob(this.getParam('job'))
        : this.parseScheduledBooking(this.getParam('booking'));
      this.pageTitle = this.buildPageTitle();
      this.rightSidePanelService.setDialogPageTitle(this.pageTitle);
      const payload = {
        link_origin_id: this.job.bookingId,
        link_origin_key: 'booking'
      };
      const response = await this.schedule.createJobLink(payload);
      this.jobLink = this.buildJobLink(response);
      this.loaded = true;
    } catch (err) {
      this.errorMessage = err?.error?.message ?? err?.message;
    }
    this.rightSidePanelService.setDialogLoading(false);
  }

  // Shape of customer/booking-details/:id, passed by JobPage for scheduled jobs.
  parseScheduledBooking(booking): ShareJobDetails {
    const currentJob = booking?.booking?.current_job;
    const pro = currentJob?.homekeepers?.[0];
    return {
      bookingId: booking?.booking?.id,
      serviceName: booking?.service_type_details?.name,
      date: currentJob?.start_datetime_local,
      startTime: currentJob?.start_datetime_local,
      proFirstName: pro?.first_name,
      proPhone: pro?.phone
    };
  }

  // Shape of me/jobs/cards/:id, passed by PastJobPage and PastPrivateJobPage.
  parsePastJob(job): ShareJobDetails {
    const pro = job?.homekeepers?.[0];
    return {
      bookingId: job?.booking?.id,
      serviceName: job?.job?.service_type_details?.name || job?.booking?.bookable_service?.name,
      date: job?.job?.date,
      startTime: job?.job?.start_time,
      proFirstName: pro?.first_name,
      proPhone: pro?.phone
    };
  }

  buildPageTitle(): string {
    const translate = new TranslationPipe();
    if (!this.job.proFirstName) {
      return translate.transform('Send Link');
    }
    return translate.transform('Send Link to ') + this.job.proFirstName;
  }

  buildJobLink(sharedLinkResponse): string {
    if (this.isPastJobLink) {
      return `www.tidy.com/past-shared-job/${sharedLinkResponse.uuid}`;
    }
    return `pro.tidy.com/#/job/${sharedLinkResponse.uuid}`;
  }

  // Params come from the caller only. Falling back to TidyStorage here resurrected
  // the booking of a previously shared job and generated a link for the wrong job.
  getParam(paramName: string) {
    const param = this.dialogParams?.[paramName];
    if (param !== null && param !== undefined) {
      return param;
    }
    return this.navCtrl.getParam(paramName);
  }

  copyLink() {
    const textArea = document.createElement('textarea');
    textArea.value = this.jobLink;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    this.copied = true;
    setTimeout(() => this.copied = false, 3000);
  }

  learnMore() {
    this.util.openUrl('https://help.tidy.com/job-links');
  }
}
